/**
 * `lib/apiError.js`
 *
 * Turn a FastAPI error payload into something a human can act on.
 *
 * ROOT CAUSE (Sentry JAVASCRIPT-REACT-G). FastAPI answers a validation
 * failure with `detail` as an ARRAY of objects:
 *   {"detail": [{"loc": ["body", "callTime"], "msg": "invalid time", ...}]}
 * Every call site in the app does `err.detail || 'Save failed'`. A JS array
 * coerced into a string becomes "[object Object],[object Object]" - so the
 * admin was shown that instead of "callTime: invalid time", and the real
 * reason the save failed was destroyed before anyone could read it. 176 call
 * sites read `.detail` this way and NONE of them handled the array.
 *
 * The error message being useless is the whole defect: a rejected save with
 * an unreadable reason is indistinguishable from a broken app.
 *
 * Never throws. Formatting an error must not be able to fail while an error
 * is already being handled.
 */
const MAX_PARTS = 3;
function _fieldOf(loc) {
  if (!Array.isArray(loc)) return '';
  // FastAPI prefixes with the source ('body', 'query', 'path'). The useful
  // part is the last string segment - the field the user actually touched.
  const parts = loc.filter((p) => typeof p === 'string' && p !== 'body' && p !== 'query' && p !== 'path');
  return parts.length ? parts[parts.length - 1] : '';
}
function _one(item) {
  if (item == null) return '';
  if (typeof item === 'string') return item.trim();
  if (typeof item !== 'object') return String(item);
  const msg = typeof item.msg === 'string' ? item.msg
    : typeof item.message === 'string' ? item.message
      : typeof item.error === 'string' ? item.error : '';
  if (!msg) return '';
  const field = _fieldOf(item.loc);
  return field ? `${field}: ${msg}` : msg;
}
export function detailToText(detail, fallback = 'Request failed') {
  try {
    if (detail == null) return fallback;
    if (typeof detail === 'string') return detail.trim() || fallback;
    if (Array.isArray(detail)) {
      const parts = detail.map(_one).filter(Boolean);
      if (!parts.length) return fallback;
      const shown = parts.slice(0, MAX_PARTS).join('; ');
      const rest = parts.length - MAX_PARTS;
      return rest > 0 ? `${shown} (+${rest} more)` : shown;
    }
    if (typeof detail === 'object') return _one(detail) || fallback;
    return String(detail) || fallback;
  } catch (e) {
    return fallback;
  }
}
/**
 * For an axios rejection: pull the best message out of the whole error.
 * Prefers the server's own words, then the transport error, then `fallback`.
 */
export function errorText(error, fallback = 'Request failed') {
  try {
    const data = error?.response?.data;
    if (data !== undefined && data !== null) {
      if (typeof data === 'string' && data.trim()) return data.trim();
      if (typeof data === 'object' && data.detail !== undefined) {
        return detailToText(data.detail, fallback);
      }
    }
    if (typeof error?.message === 'string' && error.message.trim()) return error.message.trim();
    return fallback;
  } catch (e) {
    return fallback;
  }
}
